import { useEffect, useMemo, useState } from 'react';

import { xor } from 'lodash';
import { useSnackbar } from 'notistack';

import type { MediaImage } from './ImagesVideo/types';
import type { OfferBuilderMediaController } from './useOfferBuilderMedia';

interface UseBulkImageSelectionArgs {
  deleteScopeLabel: string;
  media: OfferBuilderMediaController;
  selectableImages: Array<MediaImage>;
}

export default function useBulkImageSelection({
  deleteScopeLabel,
  media,
  selectableImages,
}: UseBulkImageSelectionArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedImagePublicIds, setSelectedImagePublicIds] = useState<Array<string>>([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const selectableImagesByPublicId = useMemo(
    () => new Map(selectableImages.map((image) => [image.publicId, image])),
    [selectableImages],
  );

  useEffect(() => {
    setSelectedImagePublicIds((current) => {
      const next = current.filter((publicId) => selectableImagesByPublicId.has(publicId));

      return next.length === current.length ? current : next;
    });
  }, [selectableImagesByPublicId]);

  const toggleSelected = (publicId: string) => {
    setSelectedImagePublicIds((current) => xor(current, [publicId]));
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);

    try {
      const publicIdsToDelete = [...selectedImagePublicIds];
      const deleteTargets = publicIdsToDelete.flatMap((publicId) => {
        const image = selectableImagesByPublicId.get(publicId);

        return image ? [{ publicId, image }] : [];
      });

      const results = await Promise.allSettled(
        deleteTargets.map(({ publicId, image }) =>
          image.sourceType === 'room'
            ? media.removeImageFromRoom(image, { showError: false, showSuccess: false })
            : media.deleteImage(publicId, { showError: false, showSuccess: false }),
        ),
      );

      const succeededPublicIds = deleteTargets
        .filter((_, index) => results[index]?.status === 'fulfilled')
        .map(({ publicId }) => publicId);
      const failedCount = publicIdsToDelete.length - succeededPublicIds.length;

      if (succeededPublicIds.length > 0) {
        enqueueSnackbar(
          `${succeededPublicIds.length} ${succeededPublicIds.length === 1 ? 'image' : 'images'} removed`,
          {
            variant: 'success',
          },
        );
      }

      if (failedCount > 0) {
        enqueueSnackbar(`${failedCount} ${failedCount === 1 ? 'image' : 'images'} failed to delete`, {
          variant: 'error',
        });
      }

      setSelectedImagePublicIds((current) => current.filter((publicId) => !succeededPublicIds.includes(publicId)));

      if (failedCount === 0) {
        setIsBulkDeleteDialogOpen(false);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const bulkDeleteDialogBody = `This will remove ${selectedImagePublicIds.length} selected ${
    selectedImagePublicIds.length === 1 ? 'image' : 'images'
  } from ${deleteScopeLabel}. This action cannot be undone.`;

  return {
    bulkDeleteDialogBody,
    isBulkDeleteDialogOpen,
    isBulkDeleting,
    selectedImagePublicIds,
    clearSelection: () => setSelectedImagePublicIds([]),
    closeBulkDeleteDialog: () => setIsBulkDeleteDialogOpen(false),
    confirmBulkDelete,
    openBulkDeleteDialog: () => setIsBulkDeleteDialogOpen(true),
    toggleSelected,
  };
}
